import { useState, useCallback } from 'react';
import { useDispatch, useParams, useSelector, useLocation, history } from 'umi';
import { Form } from 'iglooform';
import { useMount } from 'ahooks';
import { useIntl } from 'react-intl';
import PageFrame from '@/components/page-frame';
import LeaveConfirm from '@/components/leave-confirm';
import { set } from 'lodash';
import useGetFormConfig from './useGetFormConfig';
import { transformAdditionalData } from './common';
import SkeletonLoading from './components/skeleton-loading';
import styles from './index.less';

const Share = () => {
  const { formatMessage } = useIntl();
  const params = useParams<{ type: string }>();
  const form = Form.useForm();
  const dispatch = useDispatch();
  const location = useLocation();

  const initShouldConfirm = (location as any)?.query?.isBuyNowFlow !== 'true';
  const [shouldConfirm, setShouldConfirm] =
    useState<boolean>(initShouldConfirm);
  const { externalId, fastQuote, plan } = useSelector<
    ReduxState,
    ApplicationState
  >((state) => state.application);

  const { config: applicationConfig, loading } = useGetFormConfig(
    form,
    plan,
    fastQuote,
  );

  useMount(() => {
    dispatch({
      type: 'application/getFastQuotation',
      payload: {
        quotationId: (location as any)?.query?.quotationId,
      },
    });

    window.gtag('event', `application_step1`, {
      product_key: (location as any)?.query?.productKey,
      plan_key: (location as any)?.query?.planKey,
      quote_id: (location as any)?.query?.quotationId,
      user_unique_id: JSON.parse(localStorage.getItem('user_info') ?? '{}')?.id,
    });
  });

  const handleSubmit = useCallback(
    (values: any) => {
      const type = params.type;
      const applicationJson = { ...values };
      delete applicationJson.promotions;

      // hc-bsh、pa-bsh 特殊逻辑
      if (
        ['health', 'pa'].includes(type) &&
        (plan.name || '').includes('BSH')
      ) {
        const insuredObj = { ...applicationJson['additional_insured'] };
        const reqArr = transformAdditionalData(insuredObj);
        delete applicationJson['additional_insured'];
        set(applicationJson, ['additional_insured', 'insured_people'], reqArr);
      }

      setShouldConfirm(false);
      dispatch({
        type: 'application/submit',
        payload: {
          productType: params.type,
          productKey: (location as any)?.query?.productKey,
          planKey: (location as any)?.query?.planKey,
          data: {
            application: applicationJson,
            fast_quote: { ...fastQuote },
            external_id: externalId,
          },
          successCallback: (res: any) => {
            history.replace({
              pathname: `/home/${type}/payment`,
              query: {
                applicationId: res?.object?.id,
                productKey: res?.object?.product_key,
                planKey: res?.object?.plan_key,
                quotationId: res?.object?.quotation_id,
                policyStartDate:
                  params.type === 'travel'
                    ? fastQuote?.travel_start_date
                    : res?.object?.form?.policy?.policy_start_date,
              },
            });
            return;
          },
        },
      });

      //event tracking: application_submit
      window.gtag('event', 'application_submit', {
        product_key: (location as any)?.query?.productKey,
        plan_key: (location as any)?.query?.planKey,
        quote_id: (location as any)?.query?.quotationId,
        user_unique_id: JSON.parse(localStorage.getItem('user_info') ?? '{}')
          ?.id,
      });
    },
    [externalId, fastQuote, params.type, location],
  );

  if (!externalId)
    return (
      <PageFrame title="">
        <></>
      </PageFrame>
    );

  return (
    <PageFrame
      title={formatMessage({
        id: 'Application',
      })}
      subTitle={formatMessage({
        id: 'All fields are required unless marked optional.  ',
      })}
    >
      {!loading && applicationConfig ? (
        <Form
          selectDatasourceApi={TURBO_SELECT_DATA_SOURCE_API}
          className={styles.form}
          initialValues={{}}
          form={form}
          stepDirection="horizontal"
          config={applicationConfig}
          onSubmit={handleSubmit}
          locales={{
            validateMessages: {
              required: formatMessage({
                id: 'This is required',
              }),
            },
          }}
        />
      ) : (
        <SkeletonLoading />
      )}
      <LeaveConfirm
        backRedirectUrl={
          (location as any)?.query?.goBackUrl
            ? (location as any)?.query?.goBackUrl
            : '/policies'
        }
        title={formatMessage({
          id: 'You have unsaved changes on this page',
        })}
        content={formatMessage({
          id: 'If you leave this page, all unsaved changes will be lost. Are you sure you want to leave this page now?',
          defaultMessage:
            'If you leave this page, all unsaved changes will be lost. Are you sure you want to leave this page now?',
        })}
        okText={formatMessage({
          id: 'Cancel',
        })}
        cancelText={formatMessage({
          id: 'Leave',
        })}
        shouldConfirm={shouldConfirm}
        setShouldConfirm={setShouldConfirm}
      />
    </PageFrame>
  );
};

export default Share;
